import { useParams, useSearchParams } from "react-router-dom";

import useMeetingReportAcceptInvite from "@app/analytics/hooks/useReportAcceptInvite";
import useUserAttendance from "@app/analytics/MeetingReport/hooks/useUserAttendance";
import useReportShare from "@app/common/access/ShareReport/hooks/useReportShare";
import * as ShareUtils from "@app/common/access/ShareReport/utils";
import { showToast } from "@app/common/hooks/useToasts";
import { isResponseErrorWithStatus } from "@app/common/utils";

import useMeetingReport from "./useMeetingReport";
import useSessionMetadata from "./useSessionMetadata";
import useTranscript from "./useTranscript";
import useVideoRecording from "./useVideoRecording";

// simple hook that will fire off all the initial queries for the first page of the meeting report.
// intention is to avoid query waterfalls
const useMeetingReportInitialLoading = () => {
  const { sessionId } = useParams();

  const [searchParams] = useSearchParams();
  const {
    accessList,
    accessListQuery: { isLoading: isAclLoading, isError: isAclError, error },
  } = useReportShare({
    sessionId: sessionId!,
  });

  const isNotFound = isAclError && isResponseErrorWithStatus(error, 404);
  // must also be true if the report is not found
  // there are many queries and other processes that
  // are disabled when a report is unauthorized
  const isUnauthorized =
    !isAclLoading &&
    (isNotFound || ShareUtils.isForbidden(accessList.currentUserAccessLevel));

  const reportInvite = searchParams.get("mInvite");
  const { isLoading: isInviting } = useMeetingReportAcceptInvite(reportInvite, {
    enabled: !isAclLoading && isUnauthorized && Boolean(reportInvite),
    onError: () => {
      showToast({
        message:
          "Oh no! Your report invite has expired. Please request access again.",
        severity: "error",
      });
    },
  });

  const {
    isLoading: metadataLoading,
    data: { isNotetakerReport, isProcessing, isRestrictedReport },
  } = useSessionMetadata({ enabled: !isUnauthorized });

  const reportQueriesDisabled =
    isUnauthorized || isProcessing || isRestrictedReport || isNotetakerReport;

  const queryOpts = { enabled: !reportQueriesDisabled };

  const { isLoading: meetingReportLoading } = useMeetingReport({
    enabled: !isUnauthorized || isNotetakerReport,
  });

  const { isLoading: transcriptLoading } = useTranscript(queryOpts);
  const { isInitialLoading: videoRecordingLoading } =
    useVideoRecording(queryOpts);
  const { isInitialLoading: userAttendanceLoading } =
    useUserAttendance(queryOpts);

  const isDataLoading =
    meetingReportLoading ||
    metadataLoading ||
    transcriptLoading ||
    videoRecordingLoading ||
    userAttendanceLoading;

  const isInitialLoading = isInviting || isDataLoading || isAclLoading;

  return {
    isInitialLoading,
    isUnauthorized,
    isNotFound,
    reportQueriesDisabled,
  };
};

export default useMeetingReportInitialLoading;
